import { Title } from "@dropbox/dig-components/typography";
import { Stack } from "@dropbox/dig-foundations";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Click_DashProfilePage } from "@mirage/analytics/events/types/click_dash_profile_page";
import { RichTextArea } from "@mirage/mosaics/BaseProfilePage/RichTextArea/RichTextArea";
import { useDebouncedValue } from "@mirage/mosaics/BaseProfilePage/RichTextArea/useDebouncedValue";
import {
  userInfoMapAtom,
  userProfileSettingsAtom,
} from "@mirage/mosaics/UserProfilePage/atoms";
import { formatDashGetPersonResponse } from "@mirage/mosaics/UserProfilePage/hooks";
import { UserProfileDetails } from "@mirage/mosaics/UserProfilePage/UserProfileDetails";
import { callApiV2 } from "@mirage/service-dbx-api";
import { showSnackbar } from "@mirage/shared/snackbar";
import i18n, { Trans } from "@mirage/translations";
import { useAtom, useSetAtom } from "jotai";
import { EditorState } from "lexical";
import { useEffect, useRef, useState } from "react";

import type {
  UserInfo,
  UserProfileSettings,
} from "@mirage/mosaics/UserProfilePage/types";

interface EditProfileBodyProps {
  user: UserInfo;
}

export const EditUserProfileBody = ({ user }: EditProfileBodyProps) => {
  const setUserInfoMap = useSetAtom(userInfoMapAtom);
  const [userProfileSettings, setUserProfileSettings] = useAtom(
    userProfileSettingsAtom,
  );
  const [aboutMeEditorState, setAboutMeEditorState] = useState<EditorState>();
  const deboucedEditorState = useDebouncedValue<EditorState | undefined>(
    aboutMeEditorState,
    1000,
  );
  const aboutMeValue = useRef(user.user_profile_settings.about_me ?? undefined);
  const { reportPapEvent } = useMirageAnalyticsContext();

  const handleEditAutoSave = async (
    newUserProfileSettings: UserProfileSettings,
  ) => {
    const resp = await callApiV2("dashPeopleUpdateProfileSettings", {
      user_profile_settings: newUserProfileSettings,
    });
    const newUserInfo = formatDashGetPersonResponse(resp.person);
    if (newUserInfo) {
      setUserProfileSettings(newUserInfo.user_profile_settings);
      setUserInfoMap((prev) => ({
        ...prev,
        [newUserInfo.display_handle]: newUserInfo,
      }));

      reportPapEvent(
        PAP_Click_DashProfilePage({
          dashActionSurface: "dash_profile_page",
          dashProfileAction: "edit_save",
          featureLine: "people",
        }),
      );

      showSnackbar({ title: i18n.t("saved") });
    }
  };

  useEffect(() => {
    const aboutMeJson = deboucedEditorState?.toString() ?? null;
    if (aboutMeJson !== null && aboutMeJson !== userProfileSettings.about_me) {
      // Update about me setting in UI
      const newUserProfileSettings = {
        ...userProfileSettings,
        about_me: aboutMeJson,
      };
      setUserProfileSettings(newUserProfileSettings);

      // Update user profile setting in backend
      handleEditAutoSave(newUserProfileSettings);
    }
  }, [userProfileSettings, setUserProfileSettings, deboucedEditorState]);

  return (
    <Stack
      gap="32"
      style={{
        maxWidth: "704px",
      }}
    >
      <Stack gap="8">
        <Title size="small" weightVariant="emphasized" tagName="span">
          {i18n.t("user_profile_highlights_string")}
        </Title>
        <UserProfileDetails user={user} isEdit />
      </Stack>
      <Stack gap="8">
        <Title size="small" weightVariant="emphasized" tagName="span">
          {i18n.t("user_profile_about_me_string")}
        </Title>
        <RichTextArea
          source="EditUserProfilePage"
          editable
          topOffset={0}
          theme="small-faint"
          placeholder={
            <Trans
              i18nKey="user_profile_about_me_placeholder"
              components={{
                tag: <span />,
              }}
            />
          }
          value={aboutMeValue.current}
          onChange={setAboutMeEditorState}
        />
      </Stack>
    </Stack>
  );
};
